// import Editor from './LexicalDemo/App';
// export default Editor;

import { useEffect, useState } from 'react';
import * as ToolbarPlugin from './plugins/ToolbarPlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import MediaPlugin from './plugins/MediaPlugin';
import LayoutPlugin from './plugins/LayoutPlugin';
import InlineImagePlugin from './plugins/InlineImagePlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { InlineImageNode } from './nodes/InlineImageNode';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { EditorStatePlugin } from './plugins/EditorStatePlugin';
import { ParagraphNode, TextNode } from 'lexical';
import { InlineFrameNode } from './nodes/InlineFrameNode';
import { VariableNode } from './nodes/VariableNode';
import { VariablesPlugin } from './plugins/VariablesPlugin';
import { LayoutContainerNode } from './plugins/LayoutPlugin/LayoutContainerNode';
import { LayoutItemNode } from './plugins/LayoutPlugin/LayoutItemNode';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { theme } from './themes/default/classes';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { NestedButtonsGeneratorResult } from '../../utils/emails';
import './themes/default/styles.css';
import { htmlImportMap } from './conversion';
import TreeViewPlugin from './plugins/TreeViewPlugin';
import { VideoNode } from './nodes/VideoNode';

export const NODES =
[
    HeadingNode,
    ParagraphNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    // CodeNode,
    TableCellNode,
    TableRowNode,
    TableNode,
    // AutoLinkNode,
    // LinkNode,
    HorizontalRuleNode,
    TextNode,
    InlineImageNode,
    InlineFrameNode,
    VariableNode,
    LayoutContainerNode,
    LayoutItemNode,
    LinkNode,
    AutoLinkNode,
    VideoNode
    // HtmlElementNode
];

type EditorProperties =
{
    value: string;
    onChange: (value: string) => void
    generateCustomButtons?: () => NestedButtonsGeneratorResult[]
    variables: string[]
    customSettings: any
    customStyles: any
    charControlHandler: any
}

const DEBUG_MODE = false;

export default function Editor(properties: EditorProperties)
{
    const { value, variables, onChange } = properties;
    if (properties.customSettings) { console.error('properties.customSettings'); }
    if (properties.customStyles) { console.error('properties.customStyles'); }
    if (properties.charControlHandler) { console.error('properties.charControlHandler'); }
    const initialConfig =
    {
        namespace: 'MyEditor',
        theme,
        onError(error: Error) { throw error; },
        nodes: NODES,
        // html: { import: constructImportMap(), export: constructExportMap() }
        html: { import: htmlImportMap }
    };
    const isSmallWidthViewport = false;
    const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
    const ref = (element: HTMLDivElement) => { if (element !== null) { setFloatingAnchorElem(element); }};
    const actions = properties.generateCustomButtons?.();
    const element =
    <LexicalComposer initialConfig={initialConfig}>
        <div className="editor-container">
            <ToolbarPlugin.Toolbar actions={actions} debugMode={DEBUG_MODE} />
            <div className="editor-inner">
                <RichTextPlugin contentEditable={<div ref={ref}><ContentEditable className="editor-input" /></div>} ErrorBoundary={LexicalErrorBoundary} />
                <HistoryPlugin />
                {/* <AutoFocusPlugin /> */}
                <ListPlugin />
                <InlineImagePlugin />
                <TablePlugin />
                <MediaPlugin />
                <LinkPlugin />
                {floatingAnchorElem && !isSmallWidthViewport && (<DraggableBlockPlugin anchorElem={floatingAnchorElem} />)}
                <VariablesPlugin variables={variables} />
                <EditorStatePlugin initialValue={value} onChange={onChange} />
                <LayoutPlugin />
                {DEBUG_MODE && <TreeViewPlugin />}
            </div>
        </div>
    </LexicalComposer>;
    return element;
}